import { HourSelectorType, PageConfigWithCharts } from '../../../shared/features/ng-redux/page/page.models';

import { buildChartOptions, patchCard } from './app-mapper.utils';
import { ProfilePermissionEnum } from '../../../../../../../libs/services';
import {
  BarChartConfig,
  ChartTypeEnum,
  DonutChartConfig,
  GroupedLineChartConfig,
  LineChartConfig,
  TimelineChartConfig,
} from '../../../shared/models/chart.models';
import { CHART_UI_BAR_TOPN, DEVICE_SIGNAL_STRENGTH } from './app-mapper.constants';
import { HighchartsConstant } from '../../../../../../xs-colors';

export function patchDeviceLastKnownPage(page: PageConfigWithCharts) {
  page.claims = [ProfilePermissionEnum.ProfileViewLastLocationDashboard];
  if (page.charts) {
    patchCard(page.charts, 'device-lk-map', card => {
      card.icon = 'insight-icon-last-known';
      card.iconTooltip = 'sdd_last_location_icon_tooltip';
      card.type = 'last-known-map';
      card.noDataIcon = 'insight-icon-nolocation';
      card.cardStyle = ['static', 'card-2xFH'];
      card.name = 'sdd_last_location_name';
    });
  }

  page.icon = 'insight-icon-last-known';
  page.name = 'sdd_last_location_name';
}

export function patchDeviceSignalStrengthPage(page: PageConfigWithCharts) {
  page.claims = [ProfilePermissionEnum.ProfileViewSignalStrengthDashboard];
  page.multiHourSelector = true;
  page.hourEnabled = true;
  page.hourSelectorType = HourSelectorType.CHECKBOX;

  if (page.charts) {
    page.predefinedCharts = [
      {
        category: {
          name: 'Siganl_Strength_Charts_Category_Header_Summary',
          icon: 'insight-icon-battery-usage',
          tooltip: 'Signal_Strength_Charts_Category_header_Summary_Information_Tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'device-ss-heatmap', 'Device_Charts_Category_Chart_Caption_heatmap'),
          buildChartOptions(page.charts, 'device-ss-ss', 'Device_Charts_Category_Chart_Caption_SS_SS'),
          buildChartOptions(page.charts, 'device-ss-conn', 'Device_Charts_Category_Chart_Caption_SS_Connection'),
          buildChartOptions(page.charts, 'device-ss-ntt', 'Device_Charts_Category_Chart_Caption_SS_NTT'),
          buildChartOptions(page.charts, 'device-ss-do', 'Device_Charts_Category_Chart_Caption_SS_Total_DO'),
          buildChartOptions(page.charts, 'device-ss-ss-ts', 'Device_Charts_Category_Chart_Caption_SS_AVG_SS'),
          buildChartOptions(page.charts, 'device-ss-sq', 'Device_Charts_Category_Chart_Caption_SS_AVG_SQ'),
          buildChartOptions(page.charts, 'device-ss-snr', 'Device_Charts_Category_Chart_Caption_SS_AVG_SNR'),
        ],
      },
    ];

    patchCard(page.charts, 'device-ss-heatmap', card => {
      card.icon = 'insight-icon-signal-strength';
      card.iconTooltip = 'sdd_signal_strength_icon_tooltip';
      card.name = 'sdd_signal_strength_name';
      card.type = 'ss-map';
      card.noDataIcon = 'insight-icon-nolocation';
      card.cardStyle = ['static', 'card-2xFH'];
    });

    patchCard(page.charts, 'device-ss-ntt', (chart: LineChartConfig) => {
      chart.iconTooltip = 'ss_network_type_usage_fullname';
      chart.name = 'ss_network_type_usage_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: undefined,
        label: 'ss_network_type_usage_metric_label',
      };
      chart.metrics = [
        {
          id: 'nt_1',
        },
        {
          id: 'nt_2',
        },
        {
          id: 'nt_3',
        },
        {
          id: 'nt_4',
        },
        {
          id: 'nt_5',
        },
        {
          id: 'nt_6',
        },
        {
          id: 'nt_100',
        },
      ];

      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'ss_network_type_usage_tooltip_value',
      };

      chart.legends = [
        'ss_network_type_usage_metric_label_lte',
        'ss_network_type_usage_metric_label_2g',
        'ss_network_type_usage_metric_label_3g',
        'ss_network_type_usage_metric_label_4g',
        'ss_network_type_usage_metric_label_5g',
        'ss_network_type_usage_metric_label_iwlan',
        'ss_network_type_usage_metric_label_unknown',
      ];
      chart.formatDate = true;
      chart.integerOnly = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = false;
      chart.hourSwitch = true;
    });

    patchCard(page.charts, 'device-ss-do', (chart: LineChartConfig) => {
      chart.iconTooltip = 'ss_total_signal_dropout_fullname';
      chart.name = 'ss_total_signal_dropout_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'do',
        label: 'ss_total_signal_dropout_metric_label',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_ss_total_signal_drop_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.dateFormatStr = 'LL';
      chart.formatDate = true;
      chart.hourSwitch = true;
    });

    patchCard(page.charts, 'device-ss-ss-ts', (chart: LineChartConfig) => {
      chart.iconTooltip = 'ss_avg_signal_strength_fullname';
      chart.name = 'ss_avg_signal_strength_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'ss',
        label: 'ss_avg_signal_strength_metric_label',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_ss_avg_signal_strength_tooltip_value',
      };
      chart.formatDate = true;
      chart.legendDisabled = true;
      chart.dateFormatStr = 'LL';
      chart.allowNegativeValue = true;
      chart.hourSwitch = true;
    });

    patchCard(page.charts, 'device-ss-sq', (chart: LineChartConfig) => {
      chart.iconTooltip = 'ss_avg_signal_quality_fullname';
      chart.name = 'ss_avg_signal_quality_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'sq',
        label: 'ss_avg_signal_quality_metric_label',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_ss_avg_signal_quality_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.allowNegativeValue = true;
      chart.dateFormatStr = 'LL';
      chart.formatDate = true;
      chart.hourSwitch = true;
    });

    patchCard(page.charts, 'device-ss-snr', (chart: LineChartConfig) => {
      chart.iconTooltip = 'ss_avg_signal_noise_ratio_fullname';
      chart.name = 'ss_avg_signal_noise_ratio_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'snr',
        label: 'ss_avg_signal_noise_ratio_metric_label',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_ss_avg_signal_noise_ratio_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.allowNegativeValue = true;
      chart.dateFormatStr = 'LL';
      chart.formatDate = true;
      chart.hourSwitch = true;
    });

    patchCard<BarChartConfig>(page.charts, 'device-ss-conn', chart => {
      chart.iconTooltip = 'sdd_ss_chart_connection_tooltip';
      chart.name = 'sdd_ss_chart_connection_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.dimension = {
        dimensionId: 'name',
        label: 'sdd_ss_chart_connection_dimension_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'readings_label',
      };
      chart.tooltip = {
        label:
          "`${xsI18nPipe.transform('sdd_ss_chart_connection_tooltip_label',  xsI18nPipe.transform(this.point.name))}`",
        labelArgs: ['sdd_ss_chart_connection_tooltip_label', 'xsI18nPipe.transform(this.point.name)'],
        value:
          "`${xsI18nPipe.transform('sdd_ss_chart_connection_tooltip_value', this.point.y, (this.point.y/this.point.totalReadings*100).toFixed(2).replace(/\\.?0+$/,''), this.point.totalReadings)}`",
        valueArgs: [
          'sdd_ss_chart_connection_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.totalReadings)',
          'this.point.totalReadings',
        ],
      };
    });

    patchCard<DonutChartConfig>(page.charts, 'device-ss-ss', chart => {
      chart.iconTooltip = 'sdd-ss-signalStrength-iconTooltip';
      chart.type = ChartTypeEnum.DONUT;
      chart.name = 'sdd_data_usage_signal_name';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_ss_chart_ss_tooltip_label", xsI18nPipe.transform(this.point.name))}`',
        labelArgs: ['sdd_ss_chart_ss_tooltip_label', 'xsI18nPipe.transform(this.point.name)'],
        value:
          "`${xsI18nPipe.transform('sdd_ss_chart_ss_tooltip_value', this.point.y, (this.point.y/this.point.totalReadings*100).toFixed(2).replace(/\\.?0+$/,''), this.point.totalReadings)}`",
        valueArgs: [
          'sdd_ss_chart_ss_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.totalReadings)',
          'this.point.totalReadings',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'sdd_ss_chart_ss_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.fixedColors = [
        {
          label: '{SignalStrength_4}',
          color: HighchartsConstant.signalStrengthBucket.nodata,
        },
        {
          label: '{SignalStrength_0}',
          color: HighchartsConstant.signalStrengthBucket.nosignal,
        },
        {
          label: '{SignalStrength_3}',
          color: HighchartsConstant.signalStrengthBucket.bad,
        },
        {
          label: '{SignalStrength_2}',
          color: HighchartsConstant.signalStrengthBucket.average,
        },
        {
          label: '{SignalStrength_1}',
          color: HighchartsConstant.signalStrengthBucket.good,
        },
      ];
    });
  }

  page.icon = 'insight-icon-signal-strength';
  page.name = 'sdd_signal_strength_name';
}
